import React, { useState } from 'react';
import FileStatusIndicator from '../../../components/FileStatusIndicator';
import { toReadableSize, downloadFile } from '../../../utils/helpers';
import { ArrowPathIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { Button } from '../../ui';

function ErrorDisplay({ error }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract title (first line) and remaining content
  const lines = error.split('\n');
  const title = lines[0] || error;
  const content = lines.slice(1).join('\n').trim(); // Skip first line for expanded view
  const hasContent = content.length > 0;

  if (!hasContent) {
    return (
      <div className="mt-1 text-xs text-danger font-medium">
        {title}
      </div>
    );
  }

  return (
    <div className="mt-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="flex items-center gap-1.5 text-xs text-danger hover:text-danger/80 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronUpIcon className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
        )}
        <span className="font-medium">{title}</span>
      </button>
      {isExpanded && (
        <div className="mt-2 px-3 py-2 bg-danger-soft/30 border border-danger-border/40 rounded-md text-xs text-danger whitespace-pre-line max-w-[400px]">
          {content}
        </div>
      )}
    </div>
  );
}

export default function CompressionTable({ compressions, uploadingFile, onUploadClick, draggingTable, isLoading, hasSearchTerm, onRetryClick, retryingJobs, currentBatchFiles = [] }) {
  return (
    <div className="overflow-x-auto h-72 overflow-y-auto">
      <table className="min-w-full divide-y divide-border/50 transition-all duration-200">
        <thead className="bg-background-soft/50">
          <tr>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              File Name
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Original Size
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Compressed Size
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Improvement
            </th>
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50 bg-background-elevated">
          {/* Loading State */}
          {isLoading && (
            <tr>
              <td colSpan="5" className="px-6 py-16 text-center text-muted-foreground">
                Loading...
              </td>
            </tr>
          )}

          {/* No results state */}
          {!isLoading && compressions.length === 0 && !uploadingFile && (
            <tr className="group">
              <td colSpan="5" className="px-6 py-4 text-sm text-muted-foreground">
                <div className="flex flex-col gap-4 items-center justify-center border-2 border-dashed border-border/60 rounded-xl p-10 bg-background-soft/50">
                  {/* Differentiate between no results due to search and no compressions ever */}
                  {hasSearchTerm ? (
                    <>
                      No compressions found matching your search.
                      <span className="text-xs text-muted-foreground/80">Try a different search term.</span>
                    </>
                  ) : (
                    <>
                      <span className="text-foreground font-medium">No compressions yet.</span>
                      <Button
                        onClick={onUploadClick}
                        disabled={!!uploadingFile}
                        className="mt-2"
                      >
                        Upload your first file
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}


          {/* Uploading File Row - REMOVED as it's handled by BatchUploadProgressModal */}
          {/* {!isLoading && uploadingFile && ( ... )} */}

          {/* Compression Data Rows - Only show if not loading */}
          {!isLoading && compressions.map((compression) => {
            const isFileInCurrentBatch = currentBatchFiles.includes(compression.file_name);
            const rowClass = isFileInCurrentBatch
              ? "group bg-background-soft/70 opacity-70 pointer-events-none"
              : "group hover:bg-background-soft/50 transition-colors duration-150";
            return (
              <tr key={compression.id} className={rowClass}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span>{compression.file_name}</span>
                      {isFileInCurrentBatch ? (
                        <span className="text-xs text-info flex items-center gap-1">
                          <ClockIcon className="h-3 w-3 animate-spin" />
                          Queued
                        </span>
                      ) : compression.status === "FAILED" && compression.error ? (
                        null
                      ) : (
                        <FileStatusIndicator status={compression.status} />
                      )}
                    </div>
                    {compression.status === "FAILED" && compression.error && (
                      <ErrorDisplay error={compression.error} />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {toReadableSize(compression.original_file_size)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {toReadableSize(compression.compressed_file_size)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="mr-2 w-16 bg-muted rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full"
                        style={{
                          width: `${compression.compression_ratio * 100}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-primary font-medium">
                      {(compression.compression_ratio * 100).toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {/* Action Buttons Logic */}
                  {compression.status === "SUCCESS" && (
                    <button
                      className="text-accent hover:text-accent-hover opacity-0 group-hover:opacity-100 transition-all duration-200 disabled:opacity-0 font-medium"
                      onClick={() => downloadFile(compression.compressed_file_url)}
                    >
                      Download
                    </button>
                  )}
                  {compression.status === "FAILED" && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onRetryClick(compression.id); }}
                      disabled={retryingJobs.has(compression.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 mr-1.5 ${retryingJobs.has(compression.id) ? 'animate-spin' : ''}`} />
                      {retryingJobs.has(compression.id) ? 'Retrying...' : 'Retry'}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
} 